import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import GoldButton from '../components/GoldButton';
import SectionHeader from '../components/SectionHeader';
import ServiceCard from '../components/cards/ServiceCard';
import ProjectCard from '../components/cards/ProjectCard';
import StatCard from '../components/cards/StatCard';
import { Factory, HardHat, Building2, Anchor, Truck, ShieldCheck, ArrowRight, CheckCircle2 } from 'lucide-react';

const Home = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background with Parallax */}
        <div className="absolute inset-0 z-0">
          <div
            className="absolute inset-0 bg-cover bg-center transform scale-105"
            style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1693661391267-ad955aeeb564?q=80&w=2070&auto=format&fit=crop')",
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/40"></div>
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 pt-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div data-aos="fade-right">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/10 backdrop-blur-sm mb-8">
                <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                <span className="text-primary text-xs font-bold tracking-[0.2em] uppercase">Integrated Infrastructure Partner</span>
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-white leading-[1.1] mb-6">
                Building <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-[#E5C560] to-primary">Excellence</span> <br/>
                Since 2002
              </h1>

              <p className="text-lg text-muted-foreground mb-10 max-w-xl leading-relaxed border-l-2 border-primary/30 pl-6">
                Specializing in Steel Plants, Power Plants, Post Tensioning, Piling, and Turnkey Civil Construction. Delivering world-class infrastructure with zero compromise.
              </p>

              <div className="flex flex-wrap gap-6">
                <Link to="/services">
                  <GoldButton>Explore Services</GoldButton>
                </Link>
                <Link to="/projects">
                  <GoldButton variant="outline">View Projects</GoldButton>
                </Link>
              </div>
            </div>

            <div className="hidden lg:grid grid-cols-2 gap-6" data-aos="fade-left">
              <StatCard value="20+" label="Years Experience" icon={Building2} />
              <StatCard value="₹1000Cr+" label="Projects Executed" icon={Factory} />
              <StatCard value="100%" label="Safety Record" icon={ShieldCheck} />
              <StatCard value="500+" label="Skilled Workforce" icon={HardHat} />
            </div>
          </div>
        </div>

        {/* Scroll Down Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce text-primary/50">
          <div className="w-6 h-10 border-2 border-current rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-current rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Key Services */}
      <section className="py-24 bg-background relative">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="Our Expertise"
            subtitle="Comprehensive Infrastructure Solutions"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ServiceCard
              title="Steel & Power Plants"
              description="End-to-end civil construction for large-scale industrial facilities including SMS, Blast Furnaces, and Power Plants."
              icon={Factory}
            />
            <ServiceCard
              title="Turnkey Civil Projects"
              description="Complete project management from design to execution for industrial and commercial infrastructure."
              icon={Building2}
            />
            <ServiceCard
              title="Post Tensioning"
              description="Advanced PT solutions for bridges, silos, and high-rise structures ensuring structural integrity."
              icon={Anchor}
            />
            <ServiceCard
              title="Piling & Foundations"
              description="Specialized deep foundation works including bored piles, rock anchoring, and soil stabilization."
              icon={HardHat}
            />
          </div>
        </div>
      </section>

      {/* About Snapshot */}
      <section className="py-24 bg-card relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-primary/5 skew-x-12 transform translate-x-1/4"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div data-aos="fade-right">
              <SectionHeader
                title="Engineering The Future"
                subtitle="About TOTO INFRA"
                align="left"
                className="mb-8"
              />
              <p className="text-muted-foreground mb-6 leading-relaxed">
                TOTO INFRA has evolved from a specialized foundation company into a comprehensive infrastructure giant. We pride ourselves on tackling the most complex engineering challenges in the steel and power sectors.
              </p>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                With a fleet of owned machinery and a dedicated workforce, we deliver projects ahead of schedule while maintaining the highest standards of quality and safety.
              </p>

              <ul className="space-y-4 mb-10">
                {['Advanced Technology Integration', 'ISO 9001:2015 Certified', 'Pan-India Presence', 'In-House Design Capability'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-foreground font-medium">
                    <CheckCircle2 className="text-primary" size={20} />
                    {item}
                  </li>
                ))}
              </ul>

              <Link to="/about">
                <GoldButton variant="outline">Read Our Story</GoldButton>
              </Link>
            </div>

            <div className="relative" data-aos="fade-left">
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                <img
                  src="/assets/image 4.png"
                  alt="Boardroom"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent"></div>
                <div className="absolute bottom-8 left-8 right-8 bg-background/90 backdrop-blur-md p-6 rounded-xl border border-primary/20">
                  <p className="font-serif text-xl text-primary italic">"Quality is not an act, it is a habit."</p>
                </div>
              </div>
              {/* Decorative Element */}
              <div className="absolute -bottom-10 -right-10 w-40 h-40 border-2 border-primary/30 rounded-full"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Highlights */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-end mb-16">
            <SectionHeader
              title="Featured Projects"
              subtitle="Delivering Excellence"
              align="left"
              className="mb-0"
            />
            <a href="/projects" className="hidden md:flex items-center gap-2 text-primary font-medium hover:gap-4 transition-all">
              View All Projects <ArrowRight size={20} />
            </a>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <ProjectCard
              title="ACPP-II JSPL Angul"
              client="Jindal Steel & Power Ltd"
              location="Angul, Odisha"
              status="Ongoing"
              value="₹135 Cr"
              image="https://images.unsplash.com/photo-1761414500570-0b835cdcf3c8?q=80&w=2070&auto=format&fit=crop"
            />
            <ProjectCard
              title="Vertical Unifier Tower"
              client="L&T Construction"
              location="Palava, Mumbai"
              status="Completed"
              value="₹180 Cr"
              image="https://images.unsplash.com/photo-1649617413676-28b74f1ac4d3?q=80&w=2070&auto=format&fit=crop"
            />
            <ProjectCard
              title="Pune Metro Corridor-2"
              client="MahaMetro"
              location="Pune, Maharashtra"
              status="Ongoing"
              value="₹85 Cr"
              image="https://images.unsplash.com/photo-1687580712205-8f15015e1776?q=80&w=2070&auto=format&fit=crop"
            />
          </div>
        </div>
      </section>

      {/* HSE Banner */}
      <section className="py-20 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-[#E5C560] to-primary opacity-90"></div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <div data-aos="zoom-in">
            <ShieldCheck className="w-16 h-16 text-background mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-background mb-6">Zero-Accident, Safety-First Culture</h2>
            <p className="text-background/80 text-lg max-w-2xl mx-auto mb-10 font-medium">
              We believe that every accident is preventable. Our rigorous HSE policies ensure the safety of our people and the environment.
            </p>
            <Link to="/privacy">
              <button className="bg-background text-primary px-8 py-4 rounded-full font-bold tracking-wide hover:bg-white transition-colors shadow-lg">
                View Safety Policy
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-background relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent"></div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-serif font-bold text-white mb-8" data-aos="fade-up">
            Ready to Build the <span className="text-primary">Extraordinary?</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-12" data-aos="fade-up" data-aos-delay="100">
            Partner with TOTO INFRA for your next industrial or infrastructure project. Let's create a legacy together.
          </p>
          <div data-aos="fade-up" data-aos-delay="200">
            <Link to="/contact">
              <GoldButton className="text-lg px-10 py-5">Start a Conversation</GoldButton>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
